import InsightItem from 'components/InsightItem';
import SegmentedBar from 'components/SegmentedBar';
import { useAtomValue } from 'jotai';
import React from 'react';
import { Card } from 'reactstrap';

import { pcInsightsAtom } from './state';

const FindingInsights: React.FC = () => {
  const insightsData = useAtomValue(pcInsightsAtom);
  const insights = insightsData?.insights;
  const isLoading = insightsData?.loading;

  // Use zero values when loading to show placeholder UI
  const stats = {
    total: isLoading ? 0 : insights?.total || 0,
    passed: isLoading ? 0 : insights?.passed || 0,
    failed: isLoading ? 0 : insights?.failed || 0,
    error: isLoading ? 0 : insights?.error || 0,
    exception: isLoading ? 0 : insights?.exception || 0,
    urgent: isLoading ? 0 : insights?.urgent || 0,
    critical: isLoading ? 0 : insights?.critical || 0,
    serious: isLoading ? 0 : insights?.serious || 0,
    medium: isLoading ? 0 : insights?.medium || 0,
    minimal: isLoading ? 0 : insights?.minimal || 0,
    uniqueControls: isLoading ? 0 : insights?.unique_controls || 0,
    uniqueAssets: isLoading ? 0 : insights?.unique_assets || 0,
    uniqueTechnologies: isLoading ? 0 : insights?.unique_technologies || 0,
    uniqueCategories: isLoading ? 0 : insights?.unique_categories || 0,
    uniquePolicies: isLoading ? 0 : insights?.unique_policies || 0,
    uniqueUnreadNotesCount: isLoading
      ? 0
      : insights?.unique_unread_notes_count || 0,
    watchingByMeCount: isLoading ? 0 : insights?.watching_by_me_count || 0,
    assignedToMeCount: isLoading ? 0 : insights?.assigned_to_me_count || 0,
  };

  return (
    <div className="container-fluid border border-light rounded p-0 mb-1">
      <Card
        className="mb-0 px-3 py-1"
        style={{ backgroundColor: 'rgba(34, 42, 66, 0.02)' }}
      >
        <div className="d-flex flex-wrap align-items-center">
          {/* Compliance Status */}
          <div
            className="mr-2 pr-2 flex-grow-1 border-right"
            style={{ borderColor: '#dee2e6' }}
          >
            <h6 className="mb-0">Status:</h6>
            <SegmentedBar
              height="5px"
              width="100%"
              segments={[
                {
                  color: 'fail' as const,
                  value: stats.failed,
                  label: 'Failed',
                },
                {
                  color: 'pass' as const,
                  value: stats.passed,
                  label: 'Passed',
                },
                ...(stats.error > 0
                  ? [
                      {
                        color: 'warning' as const,
                        value: stats.error,
                        label: 'Error',
                      },
                    ]
                  : []),
                ...(stats.exception > 0
                  ? [
                      {
                        color: 'dark' as const,
                        value: stats.exception,
                        label: 'Exception',
                      },
                    ]
                  : []),
              ]}
              labelDisplay="value"
              tooltip={`Failed: ${stats.failed}, Passed: ${stats.passed}${
                stats.error > 0 ? `, Error: ${stats.error}` : ''
              }${stats.exception > 0 ? `, Exception: ${stats.exception}` : ''}`}
            />
          </div>

          {/* Severity */}
          <div
            className="mr-2 pr-2 flex-grow-1 border-right"
            style={{ borderColor: '#dee2e6' }}
          >
            <h6 className="mb-0">Severity:</h6>
            <SegmentedBar
              height="5px"
              width="100%"
              segments={[
                {
                  color: 'urgent',
                  value: stats.urgent,
                  label: 'Urgent',
                },
                {
                  color: 'critical',
                  value: stats.critical,
                  label: 'Critical',
                },
                {
                  color: 'serious',
                  value: stats.serious,
                  label: 'Serious',
                },
                {
                  color: 'medium',
                  value: stats.medium,
                  label: 'Medium',
                },
                {
                  color: 'minimal',
                  value: stats.minimal,
                  label: 'Minimal',
                },
              ]}
              labelDisplay="value"
              tooltip={`Urgent: ${stats.urgent}, Critical: ${stats.critical}, Serious: ${stats.serious}, Medium: ${stats.medium}, Minimal: ${stats.minimal}`}
            />
          </div>

          {/* Count Summaries */}
          <InsightItem
            label="Policies *"
            value={stats.uniquePolicies}
            tooltip="Policies with findings"
          />
          <InsightItem label="Controls" value={stats.uniqueControls} />
          <InsightItem label="Technologies" value={stats.uniqueTechnologies} />
          <InsightItem label="Categories" value={stats.uniqueCategories} />
          {stats.watchingByMeCount > 0 && (
            <InsightItem label="Watching" value={stats.watchingByMeCount} />
          )}
          {stats.assignedToMeCount > 0 && (
            <InsightItem label="Assigned" value={stats.assignedToMeCount} />
          )}
          {stats.uniqueUnreadNotesCount > 0 && (
            <InsightItem
              label="Unread Notes"
              value={stats.uniqueUnreadNotesCount}
            />
          )}
          <InsightItem label="Assets" value={stats.uniqueAssets} isLast />
        </div>
      </Card>
    </div>
  );
};

export default FindingInsights;
